/**
 * /admin/collections — single-screen CRUD for curated_collections.
 *
 * Use cases:
 *  - Seed the first "family-estate-collection" with one click.
 *  - Spin up new themed bundles (couples-only, pet-friendly, workation,
 *    seniors-friendly, etc.) without a code change.
 *  - Edit title, tagline, description, badge, CTA, hero image, active flag.
 *  - Drag-reorder the stays in the collection (simple up/down for now).
 */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Save, Trash2, ArrowUp, ArrowDown, X, ExternalLink, Sparkles, Power } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const AdminCollections = () => {
  const [pw, setPw] = useState('');
  const [authed, setAuthed] = useState(false);
  const [collections, setCollections] = useState([]);
  const [allStays, setAllStays] = useState([]);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/admin/collections/list?password=${encodeURIComponent(pw)}`);
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail || 'Failed to load');
      setCollections(j.collections || []);

      const r2 = await fetch(`${API}/api/founders/links`);
      const j2 = await r2.json();
      setAllStays(j2.stays || []);
    } catch (e) {
      setStatus(`✗ ${e.message}`);
      // password rejected → bounce back to login form by clearing local pw
      if (e.message.toLowerCase().includes('password')) setPw('');
    } finally {
      setLoading(false);
    }
  };

  // Initial load gets triggered explicitly from the login handler instead of
  // a useEffect-on-authed dependency — keeps the linter's set-state-in-effect
  // rule from incorrectly chaining login() → useEffect → setLoading.
  const login = () => {
    if (!pw) return;
    setAuthed(true);
    refresh();
  };

  const seedFamily = async () => {
    setStatus('Seeding…');
    try {
      const r = await fetch(`${API}/api/admin/collections/seed-family?password=${encodeURIComponent(pw)}`, { method: 'POST' });
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail || 'Seed failed');
      setStatus(`✓ ${j.action}: ${j.resolved_count} stays linked${j.missing?.length ? `, missing: ${j.missing.join(', ')}` : ''}`);
      refresh();
    } catch (e) { setStatus(`✗ ${e.message}`); }
  };

  const save = async () => {
    if (!editing) return;
    setStatus('Saving…');
    try {
      const r = await fetch(`${API}/api/admin/collections/upsert?password=${encodeURIComponent(pw)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editing),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail || 'Save failed');
      setStatus(`✓ ${j.action}`);
      setEditing(null);
      setCreating(false);
      refresh();
    } catch (e) { setStatus(`✗ ${e.message}`); }
  };

  const remove = async (slug) => {
    if (!window.confirm(`Delete "${slug}"? Stays are NOT affected — only this collection wrapper is removed.`)) return;
    try {
      const r = await fetch(`${API}/api/admin/collections/${slug}?password=${encodeURIComponent(pw)}`, { method: 'DELETE' });
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail || 'Delete failed');
      setStatus(`✓ deleted ${slug}`);
      refresh();
    } catch (e) { setStatus(`✗ ${e.message}`); }
  };

  const moveStay = (idx, dir) => {
    if (!editing) return;
    const ids = [...(editing.stay_ids || [])];
    const ni = idx + dir;
    if (ni < 0 || ni >= ids.length) return;
    [ids[idx], ids[ni]] = [ids[ni], ids[idx]];
    setEditing({ ...editing, stay_ids: ids });
  };
  const removeStay = (gid) => {
    setEditing({ ...editing, stay_ids: (editing.stay_ids || []).filter(g => g !== gid) });
  };
  const addStay = (gid) => {
    if (!gid || (editing.stay_ids || []).includes(gid)) return;
    setEditing({ ...editing, stay_ids: [...(editing.stay_ids || []), gid] });
  };

  if (!authed) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50 px-6">
        <div className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-sm">
          <h1 className="text-xl font-bold text-stone-900 mb-1" style={{ fontFamily: 'Playfair Display, serif' }}>Collections — admin</h1>
          <p className="text-xs text-stone-500 mb-5">Manage themed stay bundles (Family, Couples, Pet-friendly, …)</p>
          <input type="password" value={pw} onChange={(e) => setPw(e.target.value)}
                 onKeyDown={(e) => e.key === 'Enter' && login()}
                 placeholder="Admin password"
                 className="w-full border border-stone-300 rounded px-3 py-2 mb-3 text-sm"
                 data-testid="collections-pw" />
          <button onClick={login} className="w-full bg-stone-900 text-white py-2 rounded text-sm font-medium hover:bg-stone-800"
                  data-testid="collections-login">Enter</button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50">
      <header className="bg-stone-900 text-white px-6 py-6">
        <div className="max-w-6xl mx-auto flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold" style={{ fontFamily: 'Playfair Display, serif' }}>Collections</h1>
            <p className="text-stone-300 text-sm mt-1">Themed stay bundles — surface on the homepage strip + their own `/collection/&lt;slug&gt;` landing.</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={seedFamily}
                    className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-500 px-3 py-2 rounded text-sm"
                    data-testid="seed-family"
                    title="Bootstrap the Family Estate Collection in one click (idempotent)">
              <Sparkles size={14}/> Seed Family
            </button>
            <button onClick={() => {
              setCreating(true);
              setEditing({ name: '', slug: '', tagline: '', description: '', hero_image: '', angle: '', badge: '', cta: '', stay_ids: [], active: true, sort_order: 100 });
            }} className="inline-flex items-center gap-2 bg-white text-stone-900 px-3 py-2 rounded text-sm font-medium hover:bg-stone-100"
                    data-testid="new-collection">
              <Plus size={14}/> New collection
            </button>
          </div>
        </div>
      </header>

      {status && <div className="max-w-6xl mx-auto mt-4 px-6 text-sm text-stone-600">{status}</div>}

      <div className="max-w-6xl mx-auto px-6 py-10">
        {loading && <p className="text-stone-500">Loading…</p>}
        {!loading && collections.length === 0 && (
          <div className="bg-white rounded-xl border border-stone-200 p-10 text-center">
            <p className="text-stone-500 mb-4">No collections yet.</p>
            <button onClick={seedFamily} className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-500 text-white px-4 py-2 rounded text-sm">
              <Sparkles size={14}/> Seed the Family Estate Collection
            </button>
          </div>
        )}

        <div className="grid gap-4">
          {collections.map((c) => (
            <div key={c.slug} className="bg-white rounded-xl border border-stone-200 p-5 flex items-start gap-4">
              {c.hero_image && <img src={c.hero_image} alt={c.name} className="w-24 h-24 rounded-lg object-cover flex-shrink-0"/>}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="text-lg font-bold text-stone-900" style={{ fontFamily: 'Playfair Display, serif' }}>{c.name}</h3>
                  {!c.active && <span className="text-[10px] uppercase tracking-wide bg-stone-200 text-stone-600 px-1.5 py-0.5 rounded">Inactive</span>}
                  {c.badge && <span className="text-[10px] uppercase tracking-wide bg-emerald-100 text-emerald-800 px-1.5 py-0.5 rounded">{c.badge}</span>}
                </div>
                <p className="text-sm text-stone-500 italic mt-0.5">{c.tagline}</p>
                <p className="text-xs text-stone-400 mt-1">
                  /collection/{c.slug} · {c.resolved_count} of {(c.stay_ids||[]).length} stays active · sort={c.sort_order}
                </p>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <Link to={`/collection/${c.slug}`} target="_blank"
                      className="inline-flex items-center gap-1 text-[#2C5F8D] text-xs px-2 py-1.5 hover:bg-stone-100 rounded"
                      data-testid={`open-collection-${c.slug}`}>
                  <ExternalLink size={12}/> View
                </Link>
                <button onClick={() => { setCreating(false); setEditing({ ...c }); }}
                        className="text-xs px-3 py-1.5 bg-stone-100 hover:bg-stone-200 rounded"
                        data-testid={`edit-${c.slug}`}>Edit</button>
                <button onClick={() => remove(c.slug)}
                        className="text-xs px-2 py-1.5 text-red-600 hover:bg-red-50 rounded"
                        data-testid={`delete-${c.slug}`}>
                  <Trash2 size={14}/>
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Edit drawer */}
      {editing && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-end sm:items-center sm:justify-center">
          <div className="bg-white w-full sm:max-w-3xl sm:rounded-2xl max-h-[90vh] overflow-y-auto">
            <header className="px-6 py-4 border-b border-stone-200 flex items-center justify-between sticky top-0 bg-white">
              <h3 className="font-bold text-stone-900" style={{ fontFamily: 'Playfair Display, serif' }}>
                {creating ? 'New collection' : `Editing: ${editing.name}`}
              </h3>
              <button onClick={() => { setEditing(null); setCreating(false); }} className="p-1 hover:bg-stone-100 rounded">
                <X size={18}/>
              </button>
            </header>

            <div className="px-6 py-5 grid gap-4">
              <Field label="Name (e.g. The Family Estate Collection)">
                <input value={editing.name || ''} onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                       className="w-full border border-stone-300 rounded px-3 py-2 text-sm" data-testid="field-name"/>
              </Field>
              <Field label="Slug (URL: /collection/<slug>)">
                <input value={editing.slug || ''} onChange={(e) => setEditing({ ...editing, slug: e.target.value })}
                       placeholder="auto-generated from name if blank"
                       className="w-full border border-stone-300 rounded px-3 py-2 text-sm font-mono" data-testid="field-slug"/>
              </Field>
              <Field label="Tagline (one line, italic on the hero)">
                <input value={editing.tagline || ''} onChange={(e) => setEditing({ ...editing, tagline: e.target.value })}
                       className="w-full border border-stone-300 rounded px-3 py-2 text-sm" data-testid="field-tagline"/>
              </Field>
              <Field label="Description (story strip — supports paragraphs separated by blank lines)">
                <textarea value={editing.description || ''} onChange={(e) => setEditing({ ...editing, description: e.target.value })}
                          rows={5}
                          className="w-full border border-stone-300 rounded px-3 py-2 text-sm" data-testid="field-description"/>
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Badge (small chip on hero)">
                  <input value={editing.badge || ''} onChange={(e) => setEditing({ ...editing, badge: e.target.value })}
                         className="w-full border border-stone-300 rounded px-3 py-2 text-sm" data-testid="field-badge"/>
                </Field>
                <Field label="Marketing angle (family / couples / pets / workation)">
                  <input value={editing.angle || ''} onChange={(e) => setEditing({ ...editing, angle: e.target.value })}
                         className="w-full border border-stone-300 rounded px-3 py-2 text-sm" data-testid="field-angle"/>
                </Field>
                <Field label="Hero image URL (leave blank → uses first stay's hero)">
                  <input value={editing.hero_image || ''} onChange={(e) => setEditing({ ...editing, hero_image: e.target.value })}
                         className="w-full border border-stone-300 rounded px-3 py-2 text-sm font-mono" data-testid="field-hero"/>
                </Field>
                <Field label="Sort order (lower = appears first on homepage)">
                  <input type="number" value={editing.sort_order || 0} onChange={(e) => setEditing({ ...editing, sort_order: parseInt(e.target.value) || 0 })}
                         className="w-full border border-stone-300 rounded px-3 py-2 text-sm" data-testid="field-sort"/>
                </Field>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <input id="active" type="checkbox" checked={!!editing.active} onChange={(e) => setEditing({ ...editing, active: e.target.checked })} data-testid="field-active"/>
                <label htmlFor="active" className="flex items-center gap-1"><Power size={12}/> Active (visible on homepage + public page)</label>
              </div>

              {/* Stay ordering */}
              <div>
                <h4 className="text-sm font-semibold text-stone-800 mb-2">Stays in this collection ({(editing.stay_ids || []).length})</h4>
                <div className="space-y-1">
                  {(editing.stay_ids || []).map((gid, i) => {
                    const stay = allStays.find(s => s.gohimalayan_id === gid);
                    return (
                      <div key={gid} className="flex items-center gap-2 p-2 bg-stone-50 rounded text-sm">
                        <span className="text-xs text-stone-400 font-mono w-6">{i+1}.</span>
                        <span className="flex-1 truncate">{stay?.display_name || <em className="text-red-500">missing ({gid})</em>}</span>
                        <button onClick={() => moveStay(i, -1)} disabled={i===0} className="p-1 disabled:opacity-30 hover:bg-stone-200 rounded"><ArrowUp size={12}/></button>
                        <button onClick={() => moveStay(i, 1)} disabled={i===(editing.stay_ids||[]).length-1} className="p-1 disabled:opacity-30 hover:bg-stone-200 rounded"><ArrowDown size={12}/></button>
                        <button onClick={() => removeStay(gid)} className="p-1 text-red-600 hover:bg-red-50 rounded"><X size={12}/></button>
                      </div>
                    );
                  })}
                </div>
                <div className="mt-2 flex items-center gap-2">
                  <select onChange={(e) => { addStay(e.target.value); e.target.value = ''; }}
                          className="flex-1 border border-stone-300 rounded px-2 py-1.5 text-sm" data-testid="add-stay">
                    <option value="">+ Add a stay…</option>
                    {allStays
                      .filter(s => !(editing.stay_ids || []).includes(s.gohimalayan_id))
                      .map(s => (
                        <option key={s.gohimalayan_id} value={s.gohimalayan_id}>{s.display_name}</option>
                      ))}
                  </select>
                </div>
              </div>
            </div>

            <footer className="px-6 py-4 border-t border-stone-200 flex items-center justify-end gap-3 sticky bottom-0 bg-white">
              <button onClick={() => { setEditing(null); setCreating(false); }} className="text-sm px-4 py-2 hover:bg-stone-100 rounded">Cancel</button>
              <button onClick={save} className="inline-flex items-center gap-2 bg-stone-900 hover:bg-stone-800 text-white text-sm px-4 py-2 rounded font-medium" data-testid="save-collection">
                <Save size={14}/> Save
              </button>
            </footer>
          </div>
        </div>
      )}
    </div>
  );
};

const Field = ({ label, children }) => (
  <div>
    <label className="block text-xs font-medium text-stone-500 uppercase tracking-wide mb-1">{label}</label>
    {children}
  </div>
);

export default AdminCollections;
